<template>
  <aside id="bot-editor-sidebar" :class="['relative z-30 bg-gray-800 flex flex-col transition-width duration-200 h-screen sticky top-0', isCollapsed ? 'p-2 w-14' : 'p-4 w-50']">
    <div class="flex items-center justify-center">
      <router-link id="btn-nav-logo" :to="`/app/workspace/${workspaceId}/bots`" class="flex flex-col items-center justify-center w-full">
        <img
          v-if="!isCollapsed"
          src="/logo/full-gradient-stroke.png"
          :alt="$t('common.logoAlt')"
          class="w-full h-auto object-contain max-h-12"
        />
        <img
          v-else
          src="/logo/compact-gradient-stroke.png"
          :alt="$t('common.logoAlt')"
          class="w-full h-auto object-contain max-h-8 mt-2"
        />
      </router-link>
    </div>

    <!-- Sub-navigation contestuale per Bot Editor -->
    <div class="mt-4 flex-1 overflow-y-auto">
      <nav ref="subnav" @keydown="handleSubnavKeydown" class="flex flex-col gap-1 text-sm" :aria-label="$t('botEditor.subnav.ariaLabel')" role="menu">
        <!-- HOMEPAGE Entry: links to the workspace bot list, so it has no bot subpath and no active pill -->
        <router-link
          v-for="item in homepageItems"
          :key="item.id"
          :id="item.id"
          :to="`/app/workspace/${workspaceId}/bots`"
          :class="['flex items-center rounded-md hover:bg-gray-700', isCollapsed ? '!p-1 justify-center' : 'p-2']"
          role="menuitem"
          :aria-current="isActive(botPath) ? 'true' : undefined"
          v-tooltip.right="{ value: $t(item.labelKey), disabled: !isCollapsed }"
          :aria-label="$t(item.labelKey)"
          tabindex="0"
        >
          <span :class="['material-icons', isCollapsed ? '!text-2xl' : '!text-[18px] mr-3']">{{ item.icon }}</span>
          <span v-if="!isCollapsed">{{ $t(item.labelKey) }}</span>
        </router-link>

        <!-- Separator -->
        <div class="my-2 border-t border-gray-200 dark:border-gray-700"></div>

        <!-- FUNCTIONS Section -->
        <h2 v-if="!isCollapsed" class="text-xs uppercase text-gray-400 mb-2 px-1 mt-2">{{ $t('botEditor.subnav.functions') }}</h2>
        <router-link
          v-for="item in functionsItems"
          :key="item.id"
          :id="item.id"
          :to="itemPath(item)"
          :class="['flex items-center rounded-md hover:bg-gray-700', activeClass(itemPath(item)), isCollapsed ? '!p-1 justify-center' : 'p-2']"
          role="menuitem"
          :aria-current="isActive(itemPath(item)) ? 'true' : undefined"
          v-tooltip.right="{ value: $t(item.labelKey), disabled: !isCollapsed }"
          :aria-label="$t(item.labelKey)"
          tabindex="0"
        >
          <span :class="['material-icons', isCollapsed ? '!text-2xl' : '!text-[18px] mr-3']">{{ item.icon }}</span>
          <span v-if="!isCollapsed">{{ $t(item.labelKey) }}</span>
        </router-link>

        <!-- ADVANCED FUNCTIONS Section separator -->
        <div class="my-2 border-t border-gray-200 dark:border-gray-700"></div>

        <h2 v-if="!isCollapsed" class="text-xs uppercase text-gray-400 mb-2 px-1">{{ $t('botEditor.subnav.advancedFunctions') }}</h2>
        <router-link
          v-for="item in advancedItems"
          :key="item.id"
          :id="item.id"
          :to="itemPath(item)"
          :class="['flex items-center rounded-md hover:bg-gray-700', activeClass(itemPath(item)), isCollapsed ? '!p-1 justify-center' : 'p-2']"
          role="menuitem"
          :aria-current="isActive(itemPath(item)) ? 'true' : undefined"
          v-tooltip.right="{ value: $t(item.labelKey), disabled: !isCollapsed }"
          :aria-label="$t(item.labelKey)"
          tabindex="0"
        >
          <span :class="['material-icons', isCollapsed ? '!text-2xl' : '!text-[18px] mr-3']">{{ item.icon }}</span>
          <span v-if="!isCollapsed">{{ $t(item.labelKey) }}</span>
        </router-link>

        <!-- MANAGEMENT Section separator -->
        <div class="my-2 border-t border-gray-200 dark:border-gray-700"></div>

        <h2 v-if="!isCollapsed" class="text-xs uppercase text-gray-400 mb-2 px-1">{{ $t('botEditor.subnav.management') }}</h2>
        <router-link
          v-for="item in managementItems"
          :key="item.id"
          :id="item.id"
          :to="itemPath(item)"
          :class="['flex items-center rounded-md hover:bg-gray-700', activeClass(itemPath(item)), isCollapsed ? '!p-1 justify-center' : 'p-2']"
          role="menuitem"
          :aria-current="isActive(itemPath(item)) ? 'true' : undefined"
          v-tooltip.right="{ value: $t(item.labelKey), disabled: !isCollapsed }"
          :aria-label="$t(item.labelKey)"
          tabindex="0"
        >
          <span :class="['material-icons', isCollapsed ? '!text-2xl' : '!text-[18px] mr-3']">{{ item.icon }}</span>
          <span v-if="!isCollapsed">{{ $t(item.labelKey) }}</span>
        </router-link>

      </nav>
    </div>

    <!-- Vertical edge toggle trigger -->
    <button
      id="btn-sidebar-toggle"
      @click="toggleCollapse"
      :aria-label="isCollapsed ? $t('sidebar.expand') : $t('sidebar.collapse')"
      :aria-pressed="isCollapsed.toString()"
      :aria-expanded="(!isCollapsed).toString()"
      v-tooltip.right="isCollapsed ? $t('sidebar.expand') : $t('sidebar.collapse')"
      type="button"
      class="absolute -right-2 top-0 h-full w-2 bg-gray-800 hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center cursor-pointer group"
    >
      <div class="relative flex items-center justify-center">
        <svg
          :width="10"
          :height="22"
          viewBox="0 0 10 22"
          class="text-gray-800 group-hover:text-gray-600 transition-colors duration-200"
          :class="isCollapsed ? 'translate-x-1.5' : 'rotate-180 -translate-x-1.5'"
        >
          <polygon
            points="0,0 10,11 0,22"
            fill="currentColor"
          />
        </svg>
        <span
          class="material-icons absolute !text-[22px] text-white pointer-events-none"
          :class="isCollapsed ? 'translate-x-0.5' : '-translate-x-0.5'"
        >
          {{ isCollapsed ? 'arrow_right' : 'arrow_left' }}
        </span>
      </div>
    </button>
  </aside>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';

/**
 * BotEditorNavSidebar Component
 * 
 * Collapsible sidebar navigation for the bot editor with these sections:
 * - Homepage (Bot overview/dashboard)
 * - Functions (Documents, Q&A, Analytics, Transcripts)
 * - Advanced Functions (Flows, Tools, Connectors, Prompting)
 * - Management (Settings, Preview, Deploy)
 * 
 * Features:
 * - Collapsible with vertical edge trigger
 * - localStorage persistence
 * - Active route highlighting
 * - Keyboard navigation (ArrowUp/Down, Home/End)
 * - Accessibility (ARIA attributes, role=menu)
 * - i18n integration for all labels
 * 
 * @component
 * @example
 * <BotEditorNavSidebar
 *   :bot-id="currentBotId"
 * />
 */

/**
 * Component props
 */
const props = defineProps({
  /**
   * Current bot ID for navigation links
   * @type {string}
   */
  botId: {
    type: String,
    required: true
  }
});

const route = useRoute();
const workspaceId = computed(() => route.params.workspaceId);

/**
 * Root of the current bot's editor routes; each section link appends its subpath
 * @type {import('vue').ComputedRef<string>}
 */
const botPath = computed(() => `/app/workspace/${workspaceId.value}/bots/${props.botId}`);

/**
 * Full route path of a section link
 * @param {{ subpath: string }} item - Nav item
 * @returns {string} Route path under the current bot
 */
const itemPath = (item) => `${botPath.value}/${item.subpath}`;

/*
 * Nav items, one array per section, in render order (handleSubnavKeydown walks them in this order).
 * Plain constants: nothing in them is reactive. The workspace/bot ids come from botPath and the labels
 * are resolved in the template with $t(item.labelKey), so they re-translate on a locale change.
 * The homepage item has no subpath: it links to the workspace bot list, not to a bot route.
 */
const homepageItems = [
  { id: 'btn-nav-homepage', labelKey: 'botEditor.tabs.homepage', icon: 'home' },
];

const functionsItems = [
  { id: 'btn-nav-documents',   subpath: 'storage',     labelKey: 'botEditor.tabs.documents',   icon: 'description' },
  { id: 'btn-nav-qa',          subpath: 'qa',          labelKey: 'botEditor.tabs.qa',          icon: 'help' },
  { id: 'btn-nav-analytics',   subpath: 'analytics',   labelKey: 'botEditor.tabs.analytics',   icon: 'insights' },
  { id: 'btn-nav-transcripts', subpath: 'transcripts', labelKey: 'botEditor.tabs.transcripts', icon: 'text_snippet' },
];

const advancedItems = [
  { id: 'btn-nav-tools',      subpath: 'tools',      labelKey: 'botEditor.tabs.tools',      icon: 'build' },
  { id: 'btn-nav-webhooks',   subpath: 'webhooks',   labelKey: 'botEditor.tabs.webhooks',   icon: 'webhook' },
  { id: 'btn-nav-flows',      subpath: 'flows',      labelKey: 'botEditor.tabs.flows',      icon: 'account_tree' },
  { id: 'btn-nav-connectors', subpath: 'connectors', labelKey: 'botEditor.tabs.connectors', icon: 'device_hub' },
  { id: 'btn-nav-prompting',  subpath: 'prompting',  labelKey: 'botEditor.tabs.prompting',  icon: 'auto_awesome' },
];

const managementItems = [
  { id: 'btn-nav-settings',       subpath: 'settings',       labelKey: 'botEditor.tabs.settings',      icon: 'settings' },
  { id: 'btn-nav-customize-chat', subpath: 'customize-chat', labelKey: 'botEditor.tabs.customizeChat', icon: 'palette' },
  { id: 'btn-nav-deploy',         subpath: 'deploy',         labelKey: 'botEditor.tabs.deploy',        icon: 'cloud_upload' },
];

// Refs
const subnav = ref(null);
const isCollapsed = ref(false);

/**
 * Check if a route path is currently active
 * @param {string} prefix - Route path prefix to check
 * @returns {boolean} True if current route starts with prefix
 */
const isActive = (prefix) => {
  try { 
    return route.path.startsWith(prefix); 
  } catch { 
    return false; 
  }
};

/**
 * Generate CSS classes for active route highlighting
 * Adapts to collapsed state for proper visual centering
 * @param {string} prefix - Route path prefix to check
 * @returns {string} CSS classes for active state
 */
const activeClass = (prefix) => {
  if (isActive(prefix)) {
    // When collapsed, use rounded-sm pill so icon is centered. When expanded, add horizontal padding so
    // the active background is slightly wider and centers the icon visually.
    return isCollapsed.value ? 'bg-cyan-600 text-white rounded-full p-2' : 'bg-cyan-600 text-white px-3 rounded-md';
  }
  return '';
};

/**
 * Toggle sidebar collapsed state and persist to localStorage
 */
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
  try { 
    localStorage.setItem('sidebarCollapsed', isCollapsed.value ? 'true' : 'false'); 
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Get all focusable navigation items for keyboard navigation
 * @returns {Array<HTMLElement>} Array of focusable menu items
 */
const focusableSubnavItems = () => {
  if (!subnav.value) return [];
  return Array.from(subnav.value.querySelectorAll('[role="menuitem"]'));
};

/**
 * Handle keyboard navigation in sidebar menu
 * Supports ArrowUp/Down for item navigation, Home/End for first/last item
 * @param {KeyboardEvent} e - Keyboard event
 */
const handleSubnavKeydown = (e) => {
  const items = focusableSubnavItems();
  if (!items.length) return;

  const currentIndex = items.indexOf(document.activeElement);

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      const next = items[(currentIndex + 1) % items.length];
      next.focus();
      break;
    case 'ArrowUp':
      e.preventDefault();
      const prev = items[(currentIndex - 1 + items.length) % items.length];
      prev.focus();
      break;
    case 'Home':
      e.preventDefault();
      items[0].focus();
      break;
    case 'End':
      e.preventDefault();
      items[items.length - 1].focus();
      break;
    case 'Enter':
    case ' ': // Space
      // Let router-link handle click via keyboard (Enter/Space)
      break;
    default:
      break;
  }
};

// Lifecycle
onMounted(() => {
  try {
    const stored = localStorage.getItem('sidebarCollapsed');
    if (stored) isCollapsed.value = stored === 'true';
  } catch (err) {
    // Ignore localStorage errors
  }
});
</script>

<style scoped>
/* Stile per il link attivo nella sidebar */
.router-link-exact-active {
  background-color: #0891b2; /* cyan-600 */
  color: white;
}
</style>
